import { useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { toast } from 'sonner';
import { diagnosticsAPI } from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';

export function InvoiceFileCoveragePanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showDead, setShowDead] = useState(false);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.invoiceFileCoverage();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const o = result?.overall;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Invoice File Coverage</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Classifies every db.invoices document's file reference as resolvable (a Drive
          link), media only (a raw Twilio media_url from Chief-of-Staff with no Drive
          copy yet), no reference (nothing populated), or dead location (a stored value
          that isn't a Drive link). file_url is written by the Gmail-ingestion Drive
          upload step and by Chief-of-Staff when it has already filed to its own Drive;
          other sources (pennylane, bros, manual, xero) are expected to show no
          reference throughout.
        </p>

        <Button onClick={run} disabled={loading} size="sm">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
          Run
        </Button>

        {result && (
          <>
            <div className="flex flex-wrap gap-3">
              <div className="rounded-lg border p-3 min-w-[120px]">
                <p className="text-xs font-medium text-muted-foreground mb-1">Total invoices</p>
                <p className="text-lg font-semibold">{result.total_invoices}</p>
              </div>
              <div className="rounded-lg border p-3 min-w-[120px]">
                <p className="text-xs font-medium text-muted-foreground mb-1">Resolvable</p>
                <p className="text-lg font-semibold text-green-600">{o.resolvable}</p>
                <p className="text-xs text-muted-foreground">{o.resolvable_pct}%</p>
              </div>
              <div className="rounded-lg border p-3 min-w-[120px]">
                <p className="text-xs font-medium text-muted-foreground mb-1">Media only</p>
                <p className="text-lg font-semibold text-blue-600">{o.media_only}</p>
              </div>
              <div className="rounded-lg border p-3 min-w-[120px]">
                <p className="text-xs font-medium text-muted-foreground mb-1">No reference</p>
                <p className="text-lg font-semibold text-muted-foreground">{o.no_reference}</p>
              </div>
              <div className="rounded-lg border p-3 min-w-[120px]">
                <p className="text-xs font-medium text-muted-foreground mb-1">Dead location</p>
                <p className={`text-lg font-semibold ${o.dead_location > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {o.dead_location}
                </p>
              </div>
              <div className="rounded-lg border p-3 min-w-[160px]">
                <p className="text-xs font-medium text-muted-foreground mb-1">No ref/media-only, upload attempted &amp; failed</p>
                <p className="text-lg font-semibold text-amber-600">
                  {result.no_reference_with_recorded_upload_failure}
                </p>
              </div>
            </div>

            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2">By category</p>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Category</TableHead>
                    <TableHead className="text-xs text-right">Total</TableHead>
                    <TableHead className="text-xs text-right">Resolvable</TableHead>
                    <TableHead className="text-xs text-right">Media</TableHead>
                    <TableHead className="text-xs text-right">No ref</TableHead>
                    <TableHead className="text-xs text-right">Dead</TableHead>
                    <TableHead className="text-xs text-right">Resolvable %</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(result.by_category).map(([cat, stats]) => (
                    <TableRow key={cat} className="text-xs">
                      <TableCell className="py-1.5">{cat}</TableCell>
                      <TableCell className="py-1.5 text-right font-mono tabular-nums">{stats.total}</TableCell>
                      <TableCell className="py-1.5 text-right font-mono tabular-nums text-green-600">{stats.resolvable}</TableCell>
                      <TableCell className="py-1.5 text-right font-mono tabular-nums text-blue-600">{stats.media_only}</TableCell>
                      <TableCell className="py-1.5 text-right font-mono tabular-nums">{stats.no_reference}</TableCell>
                      <TableCell className="py-1.5 text-right font-mono tabular-nums text-red-600">{stats.dead_location}</TableCell>
                      <TableCell className="py-1.5 text-right font-mono tabular-nums">{stats.resolvable_pct}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2">By source</p>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Source</TableHead>
                    <TableHead className="text-xs text-right">Total</TableHead>
                    <TableHead className="text-xs text-right">Resolvable</TableHead>
                    <TableHead className="text-xs text-right">Media</TableHead>
                    <TableHead className="text-xs text-right">No ref</TableHead>
                    <TableHead className="text-xs text-right">Dead</TableHead>
                    <TableHead className="text-xs text-right">Resolvable %</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(result.by_source).map(([src, stats]) => (
                    <TableRow key={src} className="text-xs">
                      <TableCell className="py-1.5">{src}</TableCell>
                      <TableCell className="py-1.5 text-right font-mono tabular-nums">{stats.total}</TableCell>
                      <TableCell className="py-1.5 text-right font-mono tabular-nums text-green-600">{stats.resolvable}</TableCell>
                      <TableCell className="py-1.5 text-right font-mono tabular-nums text-blue-600">{stats.media_only}</TableCell>
                      <TableCell className="py-1.5 text-right font-mono tabular-nums">{stats.no_reference}</TableCell>
                      <TableCell className="py-1.5 text-right font-mono tabular-nums text-red-600">{stats.dead_location}</TableCell>
                      <TableCell className="py-1.5 text-right font-mono tabular-nums">{stats.resolvable_pct}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {result.dead_location_samples.length > 0 && (
              <div>
                <Button variant="outline" size="sm" onClick={() => setShowDead(!showDead)}>
                  {showDead ? 'Hide' : 'Show'} {result.dead_location_samples.length} dead-location sample{result.dead_location_samples.length !== 1 ? 's' : ''}
                </Button>
                {showDead && (
                  <div className="mt-3 max-h-[300px] overflow-y-auto border rounded-md">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">Invoice ID</TableHead>
                          <TableHead className="text-xs">Category</TableHead>
                          <TableHead className="text-xs">Source</TableHead>
                          <TableHead className="text-xs">file_url</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {result.dead_location_samples.map((s, i) => (
                          <TableRow key={i} className="text-xs">
                            <TableCell className="py-1.5 font-mono">{s.id}</TableCell>
                            <TableCell className="py-1.5"><Badge variant="outline" className="text-[10px]">{s.category}</Badge></TableCell>
                            <TableCell className="py-1.5">{s.source}</TableCell>
                            <TableCell className="py-1.5 max-w-[300px] truncate" title={String(s.file_url)}>{String(s.file_url)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
